import { Inject, Injectable } from '@angular/core';
import { Tokens } from '@okta/okta-auth-js';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subscription,
  from,
  of,
  timer
} from 'rxjs';
import { filter, first, switchMap, take } from 'rxjs/operators';
import { IdentityApi, OktaService, injectFlag } from 'sustainment-component';
import { Organization, UserAccount, UserSession } from 'sustainment-component';
import { environment } from 'src/environments/environment';
import { RefreshToken } from '@okta/okta-auth-js/types/lib/oidc/types/Token';
import { TokenManagerInterface } from '@okta/okta-auth-js/types/lib/oidc/types/TokenManager';
import { NavigationEnd, Router } from '@angular/router';
import { UserAccountAction } from '../store/userAccount/user-account.action';
import { UserAccountQuery } from '../store/userAccount/user-account.query';

declare let gtag: (
  event: 'event',
  action: 'user_login',
  what: { eventCategory: 'general'; eventLabel: 'successful_login'; value: 1 }
) => void;

const KEY_ROOT = `sustainment-${environment.appName}-${environment.production ? '' : 'non-prod-'
  }`;
const KEY_LAST_ACTIVITY = `${KEY_ROOT}last-activity`;
const KEY_TOKEN_EXPIRATION_TIME = `${KEY_ROOT}token-expiration-time`;
const KEY_MODAL_STATE = `${KEY_ROOT}modal-state`;
const KEY_MODAL_DISMISS = `${KEY_ROOT}modal-dismiss`;
const KEY_LOGIN_SUCCESS = `${KEY_ROOT}login-success`;
const KEY_LOGOUT_REQUEST = `${KEY_ROOT}logout-request`;
const REFRESH_LOCK_KEY = `${KEY_ROOT}refresh-lock`;
const KEY_COMPANY_ID = `${KEY_ROOT}company-id`;
const REFRESH_LOCK_TIMEOUT = 30000;
const CROSS_TAB_EVENT_TIMEOUT = 10000;
const ACTIVITY_THROTTLE_MS = 1000;
const MAX_TIMESTAMP_DRIFT_MS = 5000;
const MODAL_CLEANUP_DELAY_MS = 500;
const LOCK_VERIFICATION_DELAY_MS = 10;
const MAX_REFRESH_WAIT_ATTEMPTS = 60;
const REFRESH_CHECK_INTERVAL_MS = 500;
const MINIMUM_REFRESH_INTERVAL_MS = 1000;

const MILLISECONDS_PER_MINUTE = 60 * 1000;
const LOGIN_CALLBACK_PATH = '/login/callback';
const LOGIN_ROUTE_PATTERN = '/login';
const REGISTRATION_ROUTE = '/registration';
const CURRENT_ROUTE_KEY = 'current-route';
const URL_PARAM_CODE = 'code';

// =============================================================================
// TOKEN REFRESH & SESSION TIMEOUT SYSTEM
// =============================================================================
//
// OVERVIEW:
// This service manages authentication tokens from three sources:
// 1. Okta - Access tokens (1 hour expiration) and refresh tokens
// 2. Identity API - Backend tokens (24 hour expiration in appKeyExpirationUtc)
// 3. Portal - Uses Okta's actual token expiration as source of truth
//
// HOW IT WORKS:
// 1. Login: User authenticates via Okta, gets tokens from Identity API
// 2. Timer: Refresh timer fires at (Okta token expiration - 10 minutes) = 50 minutes
// 3. Activity Check: If user had activity in last 5 minutes, attempt silent refresh
// 4. Silent Refresh: Renew Okta tokens → Get new Identity API tokens → Reset timer
// 5. Success: User stays logged in, timer resets for next 50 minutes
// 6. Failure: Show timeout modal, user has 10 minutes to click "Continue"
// 7. Modal Click: Triggers manual refresh, if successful user stays logged in
// 8. Timeout: If user doesn't click within 10 minutes, logout
//
// MULTI-TAB COORDINATION:
// - Uses localStorage lock to ensure only one tab performs refresh at a time
// - Other tabs wait for refresh completion and check if token expiration changed
// - Cross-tab events sync modal state and activity tracking
//
// KEY INSIGHT:
// We use Okta's actual access token expiration (1 hour) as the source of truth,
// NOT the backend's 24-hour token lifetime. This ensures we refresh every 50 minutes,
// keeping the session active indefinitely for active users while respecting Okta's
// security policies.
//
// =============================================================================

// Production timeout configuration
const PROD_TIMEOUT_WARNING = 60 * 1000 * 10;  // 10 minutes before token expires to attempt refresh
const PROD_MODAL_TIMEOUT = 60 * 1000 * 10;    // 10 minutes user has to respond to timeout modal
const PROD_ACTIVITY_THRESHOLD = 5;            // 5 minutes to consider activity "recent"

// Short timeout configuration (for testing with shortPortalTimeout flag enabled)
const SHORT_TOKEN_EXPIRATION = 30 * 1000;    // 180 seconds - overrides token lifetime for testing
const SHORT_TIMEOUT_WARNING = 90 * 1000;      // 90 seconds before token expires to attempt refresh
const SHORT_MODAL_TIMEOUT = 10 * 1000;        // 10 seconds user has to respond to timeout modal
const SHORT_ACTIVITY_THRESHOLD = 0.05;        // 0.05 minutes (3 seconds) to consider activity "recent"

@Injectable({ providedIn: 'root' })
export class AuthService {
  public readonly isAuthenticated$: Observable<boolean>;
  public readonly loadingSession$: Observable<boolean>;
  public readonly tokenExpiringSoon$: Observable<boolean>;

  private readonly _isAuthenticated = new BehaviorSubject<boolean>(false);
  private readonly _loadingSession = new BehaviorSubject<boolean>(true);
  private readonly _tokenExpiringSoon = new BehaviorSubject<boolean>(false);
  
  private _refreshTimerSubscription: Subscription;
  private _timeoutTimerSubscription: Subscription | null;
  private _callbackCode: string;
  private readonly _isShortTimeoutEnabled = injectFlag('shortPortalTimeout');


  public get timeoutWarning(): number {
    if (this._isShortTimeoutEnabled() && !environment.production) {
      return SHORT_TIMEOUT_WARNING;
    }
    return PROD_TIMEOUT_WARNING;
  }

  public get modalTimeout(): number {
    if (this._isShortTimeoutEnabled() && !environment.production) {
      return SHORT_MODAL_TIMEOUT;
    }
    return PROD_MODAL_TIMEOUT;
  }

  private parseTimestamp(value: string): number {
    return parseInt(value, 10);
  }

  private setStorageItem(key: string, value: string | number): void {
    localStorage.setItem(key, value.toString());
  }

  private getStorageTimestamp(key: string): number | null {
    const value = localStorage.getItem(key);
    return value ? this.parseTimestamp(value) : null;
  }

  private isValidRecentTimestamp(timestamp: number): boolean {
    const now = Date.now();
    const timeDiff = Math.abs(timestamp - now);
    return timeDiff <= MAX_TIMESTAMP_DRIFT_MS;
  }

  public constructor(
    private readonly _identityApi: IdentityApi,
    private readonly _userAccountAction: UserAccountAction,
    private readonly _oktaService: OktaService,
    private readonly _router: Router,
    @Inject('UserAccountQuery') private readonly _userAccountQuery: UserAccountQuery
  ) {
    if (window.location.pathname === LOGIN_CALLBACK_PATH) {
      const params = new URLSearchParams(window.location.search);
      this._callbackCode = params.get(URL_PARAM_CODE) || '';
    }

    _router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((end: NavigationEnd) => {
        if (!end.urlAfterRedirects.includes(LOGIN_ROUTE_PATTERN)) {
          this.updateLastActivity();
          if (this._isAuthenticated.value) {
            sessionStorage.setItem(CURRENT_ROUTE_KEY, end.urlAfterRedirects);
            const currentSession = this._userAccountQuery.getValue()?.userSession;
            if (currentSession?.sustainmentId) {
              sessionStorage.setItem(KEY_COMPANY_ID, currentSession.sustainmentId);
            }
          }
        }
      });

    this.setupCrossTabCommunication();
    this.setupActivityTracking();

    this.loadingSession$ = this._loadingSession.asObservable();
    this.tokenExpiringSoon$ = this._tokenExpiringSoon.asObservable();
    this.isAuthenticated$ = from(
      this._oktaService.widget.authClient.isAuthenticated()
    ).pipe(
      switchMap((isAuthenticated: boolean) => {
        if (!isAuthenticated) {
          this._loadingSession.next(false);
        } else {
          this._loadingSession.next(true);
        }

        return this._isAuthenticated.pipe(
          first(),
          switchMap((sustainmentAuthenticated) => {
            if (sustainmentAuthenticated) {
              this._loadingSession.next(false);
              return of(true);
            }
            return this.restoreSession();
          })
        );
      })
    );
  }

  private get tokenManager(): TokenManagerInterface {
    return this._oktaService.widget.authClient.tokenManager;
  }

  private setupCrossTabCommunication(): void {
    window.addEventListener('storage', (event) => {
      if (event.key === KEY_MODAL_STATE && event.newValue) {
        const modalTimestamp = this.parseTimestamp(event.newValue);
        if (!this.isValidRecentTimestamp(modalTimestamp)) {
          return;
        }

        if (this._isAuthenticated.value && !this._tokenExpiringSoon.value) {
          const currentRoute = this._router.url;
          if (currentRoute && !currentRoute.includes(LOGIN_ROUTE_PATTERN)) {
            sessionStorage.setItem(CURRENT_ROUTE_KEY, currentRoute);
            const currentSession = this._userAccountQuery.getValue()?.userSession;
            if (currentSession?.sustainmentId) {
              sessionStorage.setItem(KEY_COMPANY_ID, currentSession.sustainmentId);
            }
          }
          this._tokenExpiringSoon.next(true);
          this.startTimeoutTimer();
        }
      }

      if (event.key === KEY_MODAL_DISMISS && event.newValue && this._tokenExpiringSoon.value) {
        const dismissTimestamp = this.parseTimestamp(event.newValue);
        if (this.isValidRecentTimestamp(dismissTimestamp)) {
          this._tokenExpiringSoon.next(false);
          this.stopTimeoutTimer();
        }
      }

      if (event.key === KEY_LOGIN_SUCCESS && event.newValue) {
        const eventTime = this.parseTimestamp(event.newValue);
        if (!this.isValidRecentTimestamp(eventTime)) {
          return;
        }

        const timeSinceEvent = Date.now() - eventTime;
        if (!this._isAuthenticated.value && timeSinceEvent < CROSS_TAB_EVENT_TIMEOUT) {
          this.restoreSession();
        }
      }

      if (event.key === KEY_LOGOUT_REQUEST && event.newValue && this._isAuthenticated.value) {
        const logoutTimestamp = this.parseTimestamp(event.newValue);
        if (!this.isValidRecentTimestamp(logoutTimestamp)) {
          return;
        }

        const currentRoute = this._router.url;
        if (currentRoute && !currentRoute.includes(LOGIN_ROUTE_PATTERN)) {
          sessionStorage.setItem(CURRENT_ROUTE_KEY, currentRoute);
          const currentSession = this._userAccountQuery.getValue()?.userSession;
          if (currentSession?.sustainmentId) {
            sessionStorage.setItem(KEY_COMPANY_ID, currentSession.sustainmentId);
          }
        }
        this.closeSession(true);
        this._router.navigate([LOGIN_ROUTE_PATTERN]);
      }

      if (event.key === KEY_TOKEN_EXPIRATION_TIME && event.newValue && this._isAuthenticated.value) {
        const newExpirationDate = new Date(this.parseTimestamp(event.newValue));
        if (this._tokenExpiringSoon.value) {
          this._tokenExpiringSoon.next(false);
          this.stopTimeoutTimer();
        }
        this.startTokenRefreshInterval(newExpirationDate);
      }
    });
  }

  private setupActivityTracking(): void {
    const updateActivity = (): false | void => this._isAuthenticated.value && 
      this.setStorageItem(KEY_LAST_ACTIVITY, Date.now());

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    let lastUpdate = 0;

    events.forEach(event => {
      document.addEventListener(event, () => {
        const now = Date.now();
        if (now - lastUpdate > ACTIVITY_THROTTLE_MS && this._isAuthenticated.value) {
          lastUpdate = now;
          updateActivity();
        }
      }, { passive: true });
    });

    window.addEventListener('focus', updateActivity);
  }

  public login(oktaTokens: Tokens, isCallback?: boolean): Promise<boolean> {
    this._oktaService.widget.authClient.tokenManager.setTokens(oktaTokens);

    return from(this._oktaService.widget.authClient.token.getUserInfo())
      .pipe(
        switchMap((user) =>
          this._identityApi.getUserAccount({
            username: user.name!,
            firstName: user.given_name!,
            lastName: user.family_name!
          })
        ),
        switchMap((userAccount: UserAccount) => {
          this._userAccountAction.setUserAccount(userAccount);

          if (!userAccount.organizations?.length) {
            return this._identityApi.login(userAccount.externalId, false, isCallback);
          }

          const org = userAccount.organizations[0];
          return (org as Organization & { pending: boolean })?.pending
            ? EMPTY
            : this._identityApi.login(org.sustainmentId, false, isCallback);
        })
      )
      .toPromise()
      .then(async (userSession: UserSession | undefined | null) => {
        if (userSession === null) {
          this._isAuthenticated.next(true);
          this._loadingSession.next(false);
          this._router.navigate([REGISTRATION_ROUTE]);
          return true;
        }

        if (!userSession) return false;

        if (environment.production) {
          gtag('event', 'user_login', {
            eventCategory: 'general',
            eventLabel: 'successful_login',
            value: 1
          });
        }
        this._userAccountAction.setUserSession(userSession);
        await this.startTokenRefreshInterval(userSession.appKeyExpirationUtc);
        this._isAuthenticated.next(true);
        this._loadingSession.next(false);

        this.setStorageItem(KEY_LOGIN_SUCCESS, Date.now());
        this.updateLastActivity();

        const storedRoute = sessionStorage.getItem(CURRENT_ROUTE_KEY);
        const storedCompanyId = sessionStorage.getItem(KEY_COMPANY_ID);
        const currentCompanyId = userSession.sustainmentId;

        if (storedRoute && storedRoute !== this._router.url) {
          if (storedCompanyId !== currentCompanyId) {
            sessionStorage.removeItem(CURRENT_ROUTE_KEY);
            sessionStorage.removeItem(KEY_COMPANY_ID);
          } else {
            this._router.events.pipe(
              filter(event => event instanceof NavigationEnd),
              take(1)
            ).subscribe(() => {
              this._router.navigate([storedRoute]);
              sessionStorage.removeItem(CURRENT_ROUTE_KEY);
              sessionStorage.removeItem(KEY_COMPANY_ID);
            });
          }
        }

        sessionStorage.setItem(KEY_COMPANY_ID, currentCompanyId);

        return true;
      });
  }

  public stopTokenRefreshInterval(): void {
    if (this._refreshTimerSubscription) {
      this._refreshTimerSubscription.unsubscribe();
    }
  }

  public logout(preserveRoute: boolean = false): void {
    this.closeSession(preserveRoute);
    this.setStorageItem(KEY_LOGOUT_REQUEST, Date.now());
    this._oktaService.widget.authClient
      .signOut({
        postLogoutRedirectUri: window.location.origin + LOGIN_ROUTE_PATTERN,
      });
  }

  private isRefreshTokenValid(
    refreshToken: RefreshToken | undefined | null
  ): boolean {
    return (
      (refreshToken && !this.tokenManager.hasExpired(refreshToken)) || false
    );
  }

  public restoreSession(): Promise<boolean> {
    this._loadingSession.next(true);

    return this.tokenManager.getTokens().then((tokens: Tokens) => {
      if (this.isRefreshTokenValid(tokens.refreshToken) || this._callbackCode) {
        const isCallback = (this._callbackCode || '').length > 0;

        if (!isCallback && !this.hasRecentActivity()) {
          this._loadingSession.next(false);
          return false;
        }

        return this._oktaService.widget.authClient.token
          .renewTokens()
          .then((refreshed: Tokens) => {
            this.setStorageItem(KEY_MODAL_DISMISS, Date.now());
            return this.login(refreshed, isCallback);
          })
          .catch(() => {
            this._loadingSession.next(false);
            return false;
          });
      }
      this._loadingSession.next(false);
      return false;
    });
  }

  public continueSession(): Promise<boolean> {
    this.stopTimeoutTimer();
    this._tokenExpiringSoon.next(false);
    this.setStorageItem(KEY_MODAL_DISMISS, Date.now());

    return this.tokenManager.getTokens().then((tokens: Tokens) => {
      if (this.isRefreshTokenValid(tokens.refreshToken)) {
        return this._oktaService.widget.authClient.token
          .renewTokens()
          .then((refreshed: Tokens) => {
            this._oktaService.widget.authClient.tokenManager.setTokens(refreshed);
            return this.updateUserSessionSilently();
          })
          .then((result) => {
            if (result) {
              this.updateLastActivity();
              this._loadingSession.next(false);
            } else {
              this._tokenExpiringSoon.next(true);
              this.startTimeoutTimer();
            }
            return result;
          })
          .catch(() => {
            this.closeSession(true);
            return false;
          });
      } else {
        this.closeSession(true);
        return false;
      }
    });
  }

  private async updateUserSessionSilently(): Promise<boolean> {
    try {
      const user = await this._oktaService.widget.authClient.token.getUserInfo();
      const userAccount = await this._identityApi.getUserAccount({
        username: user.name!,
        firstName: user.given_name!,
        lastName: user.family_name!
      }).toPromise();
      
      if (!userAccount?.organizations?.length) {
        return false;
      }
      
      const org = userAccount.organizations[0];
      const userSession = await this._identityApi.login(
        org.sustainmentId,
        false,
        false
      ).toPromise();
      
      if (!userSession) {
        return false;
      }
      
      this._userAccountAction.setUserSession(userSession);
      await this.startTokenRefreshInterval(userSession.appKeyExpirationUtc);
      return true;
    } catch (error) {
      return false;
    }
  }

  private getTimeUntilExpiration(expirationTime: Date): number {
    if (this._isShortTimeoutEnabled() && !environment.production) {
      return SHORT_TOKEN_EXPIRATION;
    }

    // Use Okta access token lifetime (1 hour) instead of backend token lifetime (24 hours)
    // Okta access tokens expire at 1 hour, and must be refreshed before that
    // The Okta session (2 hours) allows multiple refreshes, but each access token only lasts 1 hour
    const OKTA_ACCESS_TOKEN_LIFETIME_MS = 60 * 60 * 1000; // 1 hour = 3600000ms
    return OKTA_ACCESS_TOKEN_LIFETIME_MS;
  }

  private async startTokenRefreshInterval(expirationTime: Date): Promise<void> {
    this.stopTokenRefreshInterval();

    const timeUntilExpiry = this.getTimeUntilExpiration(expirationTime);

    let expirationTimestamp: number;
    if (this._isShortTimeoutEnabled() && !environment.production) {
      expirationTimestamp = Date.now() + SHORT_TOKEN_EXPIRATION;
    } else {
      // Read Okta's actual token expiration time as source of truth
      try {
        const tokens = await this._oktaService.widget.authClient.tokenManager.getTokens();
        const oktaExpiration = tokens.accessToken?.expiresAt;

        if (oktaExpiration) {
          // Okta uses seconds, convert to milliseconds
          expirationTimestamp = oktaExpiration * 1000;
        } else {
          // Fallback to calculated expiration if Okta token not available
          expirationTimestamp = Date.now() + timeUntilExpiry;
        }
      } catch (error) {
        // Fallback to calculated expiration on error
        expirationTimestamp = Date.now() + timeUntilExpiry;
      }
    }
    this.setStorageItem(KEY_TOKEN_EXPIRATION_TIME, expirationTimestamp);

    if (timeUntilExpiry <= 0) {
      this.showTimeoutModal();
      return;
    }

    let refreshInterval = timeUntilExpiry - this.timeoutWarning;

    if (refreshInterval < MINIMUM_REFRESH_INTERVAL_MS) {
      refreshInterval = Math.max(Math.floor(timeUntilExpiry * 0.5), MINIMUM_REFRESH_INTERVAL_MS);

      if (refreshInterval >= timeUntilExpiry) {
        this.showTimeoutModal();
        return;
      }
    }

    this._refreshTimerSubscription = timer(refreshInterval).subscribe(async () => {
      if (this.hasRecentActivity()) {
        const refreshSucceeded = await this.attemptCoordinatedTokenRefresh();
        if (!refreshSucceeded) {
          this.showTimeoutModal();
        }
      } else {
        this.showTimeoutModal();
      }
    });

    this.updateLastActivity();
  }

  public closeSession(preserveRoute: boolean = false): void {
    this._loadingSession.next(false);
    this._oktaService.widget.authClient.tokenManager.removeRefreshToken();
    this.stopTokenRefreshInterval();
    this.stopTimeoutTimer();
    
    this._userAccountAction.removeUserSession();
    this._isAuthenticated.next(false);
    this._tokenExpiringSoon.next(false);
    
    const keysToRemove = [
      KEY_TOKEN_EXPIRATION_TIME, KEY_LAST_ACTIVITY, KEY_MODAL_STATE,
      KEY_MODAL_DISMISS, KEY_LOGIN_SUCCESS, REFRESH_LOCK_KEY
    ];
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    if (!preserveRoute) {
      sessionStorage.removeItem(CURRENT_ROUTE_KEY);
      sessionStorage.removeItem(KEY_COMPANY_ID);
    }
  }

  private showTimeoutModal(): void {
    const currentRoute = this._router.url;
    if (currentRoute && !currentRoute.includes(LOGIN_ROUTE_PATTERN)) {
      sessionStorage.setItem(CURRENT_ROUTE_KEY, currentRoute);
      const currentSession = this._userAccountQuery.getValue()?.userSession;
      if (currentSession?.sustainmentId) {
        sessionStorage.setItem(KEY_COMPANY_ID, currentSession.sustainmentId);
      }
    }

    this._tokenExpiringSoon.next(true);
    this.startTimeoutTimer();

    const timestamp = Date.now();
    this.setStorageItem(KEY_MODAL_STATE, timestamp);

    setTimeout(() => {
      if (this.getStorageTimestamp(KEY_MODAL_STATE) === timestamp) {
        localStorage.removeItem(KEY_MODAL_STATE);
      }
    }, MODAL_CLEANUP_DELAY_MS);
  }

  private startTimeoutTimer(): void {
    this.stopTimeoutTimer();
    this._timeoutTimerSubscription = timer(this.modalTimeout).subscribe(() => {
      this.logout(true);
    });
  }

  private stopTimeoutTimer(): void {
    if (this._timeoutTimerSubscription) {
      this._timeoutTimerSubscription.unsubscribe();
      this._timeoutTimerSubscription = null;
    }
  }

  public updateLastActivity(): void {
    this.setStorageItem(KEY_LAST_ACTIVITY, Date.now());
  }

  private hasRecentActivity(): boolean {
    const lastActivityTimestamp = this.getStorageTimestamp(KEY_LAST_ACTIVITY);
    if (!lastActivityTimestamp) return false;

    const timeSinceActivity = Date.now() - lastActivityTimestamp;
    const thresholdMinutes = (this._isShortTimeoutEnabled() && !environment.production)
      ? SHORT_ACTIVITY_THRESHOLD
      : PROD_ACTIVITY_THRESHOLD;
    const threshold = thresholdMinutes * MILLISECONDS_PER_MINUTE;

    return timeSinceActivity < threshold;
  }

  private async silentTokenRefresh(): Promise<boolean> {
    try {
      const tokens = await this.tokenManager.getTokens();

      if (!this.isRefreshTokenValid(tokens.refreshToken)) {
        return false;
      }

      const refreshedTokens = await this._oktaService.widget.authClient.token.renewTokens();
      this._oktaService.widget.authClient.tokenManager.setTokens(refreshedTokens);

      const user = await this._oktaService.widget.authClient.token.getUserInfo();
      const userAccount = await this._identityApi.getUserAccount({
        username: user.name!,
        firstName: user.given_name!,
        lastName: user.family_name!
      }).toPromise();

      if (!userAccount?.organizations?.length) {
        return false;
      }

      const org = userAccount.organizations[0];
      const userSession = await this._identityApi.login(
        org.sustainmentId,
        false,
        false
      ).toPromise();

      if (!userSession) {
        return false;
      }

      this._userAccountAction.setUserSession(userSession);
      await this.startTokenRefreshInterval(userSession.appKeyExpirationUtc);

      return true;
    } catch (error) {
      console.error('[AUTH] Token refresh failed:', error);
      return false;
    }
  }



  private async attemptCoordinatedTokenRefresh(): Promise<boolean> {
    const oldExpirationTime = this.getStorageTimestamp(KEY_TOKEN_EXPIRATION_TIME);
    const lockAcquired = await this.acquireLock();

    if (!lockAcquired) {
      return this.waitForRefreshCompletion(oldExpirationTime);
    }

    try {
      return await this.silentTokenRefresh();
    } finally {
      this.releaseLock();
    }
  }

  private async acquireLock(): Promise<boolean> {
    const now = Date.now();
    const existingLock = localStorage.getItem(REFRESH_LOCK_KEY);

    if (existingLock) {
      const lockTimestamp = this.parseTimestamp(existingLock);
      const lockAge = now - lockTimestamp;

      if (lockAge < REFRESH_LOCK_TIMEOUT) {
        return false;
      }
    }

    this.setStorageItem(REFRESH_LOCK_KEY, now);
    await new Promise(resolve => setTimeout(resolve, LOCK_VERIFICATION_DELAY_MS));

    return this.getStorageTimestamp(REFRESH_LOCK_KEY) === now;
  }

  private releaseLock(): void {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  }

  private waitForRefreshCompletion(oldExpirationTime: number | null): Promise<boolean> {
    return new Promise((resolve) => {
      let attempts = 0;

      const checkInterval = setInterval(() => {
        const lock = localStorage.getItem(REFRESH_LOCK_KEY);
        const newExpirationTime = this.getStorageTimestamp(KEY_TOKEN_EXPIRATION_TIME);

        if (!lock) {
          clearInterval(checkInterval);
          const refreshSucceeded = newExpirationTime !== null && newExpirationTime !== oldExpirationTime;
          resolve(refreshSucceeded);
          return;
        }

        if (++attempts >= MAX_REFRESH_WAIT_ATTEMPTS) {
          clearInterval(checkInterval);
          resolve(false);
        }
      }, REFRESH_CHECK_INTERVAL_MS);
    });
  }
}
